import React, { useCallback, useReducer, useEffect } from "react";
import { useForm } from "react-hook-form";
import { getSims, deleteSim } from "../../services/sims/simsService"

import CustomPaginator from "../../shared/components/paginator/CustomPaginator";
import ButtonNew from "../../shared/components/buttonNew/ButtonNew";
import InputSearch from "../../shared/components/inputSearch/InputSearch";
import SelectStatus from "../../shared/components/selectStatus/SelectStatus";
import ButtonFilter from "../../shared/components/buttonFilter/ButtonFilter";
import ButtonDownload from "../../shared/components/buttonDownload/ButtonDownload";
import ButtonEdit from "../../shared/components/buttonEdit/ButtonEdit";
import ButtonDelete from "../../shared/components/buttonDelete/ButtonDelete";
import DeleteModal from "../../shared/components/deleteModal/DeleteModal";
import TableHeader from "../../shared/components/tableHeader/TableHeader";

const ITEMS_PER_PAGE = 10;
const TABLE_HEADERS = ['ID SimCard','ICCID','Activación','Estado', 'Acciones'];
const STATUS_OPTIONS = [
    { label: 'Activo', value: 'Activo', },
    { label: 'Inactivo', value: 'Inactivo' },
    { label: 'dañado', value: 'dañado' },
    { label: 'testing', value: 'Testing', },    
];

// Reducer
const initialState = {
    sims: [],
    currentPage: 1,
    totalPages: 3,
    isLoading: false,
    error: null,
    deleteModalOpen: false,
    selectedSimId: null,
    filters: { emailFilter: '', statusFilter: '' }
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_USERS':
            return { ...state, sims: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_PAGE':
            return { ...state, currentPage: action.payload };
        case 'SET_TOTAL_PAGES':
            return { ...state, totalPages: action.payload };
        case 'SET_FILTERS':
            return { ...state, filters: action.payload };
        case 'TOGGLE_DELETE_MODAL':
            return { 
                ...state, 
                deleteModalOpen: !state.deleteModalOpen,
                selectedSimId: action.payload 
            };
        default:
            return state;
    }
}

// Components
const SimTable = React.memo(({ sims, onDelete, error }) => {
    if (error) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                    Error obteniendo los datos
                </td>
            </tr>
        );
    }
    if (sims.length === 0) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
                <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                    No se encontraron registros
                </td>
            </tr>
        );
    }

    return sims.map((sim) => (
        <tr key={sim.id} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
            <th scope="row" className="px-6 py-3 font-medium whitespace-nowrap">
                {sim.id} 
            </th>
            
            <td className="px-6 py-3">
                {sim.iccid}
            </td>

            <td className="px-6 py-3">
                {sim.activationDate.split('T')[0]}
            </td>

            <td className="px-6 py-3">
                {sim.status}
            </td>

            <td className="flex px-6 py-3">
                <ButtonEdit to={`/simcards/${sim.id}`}></ButtonEdit>
                <ButtonDelete onClick={() => onDelete(sim.id)}></ButtonDelete>
            </td>
        </tr>
    ));
});

const FilterForm = React.memo(({ onSubmit, register }) => (
    <form onSubmit={onSubmit} className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 mb-2">
        <div className="flex items-center w-full md:w-1/3">
            <InputSearch
                placeholder="ICCID" 
                name="iccidFilter" 
                id="iccidFilter"
                {...register('iccidFilter')}
            />
            
        </div>
        <div className="flex items-center w-full md:w-1/4">
            <SelectStatus
                id="statusFilter" 
                name="statusFilter"
                placeholder="Seleccionar estado"
                options={STATUS_OPTIONS}
                {...register('statusFilter')}
            />
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-end w-full md:w-1/3 space-y-3 sm:space-y-0 sm:space-x-4">
            <ButtonFilter type="submit" />
            <ButtonDownload />
        </div>
    </form>
));


const Simcards = () => {

    const [state, dispatch] = useReducer(reducer, initialState);
    const { register, handleSubmit } = useForm();

    const loadSims = useCallback(async (filters = {}) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            dispatch({ type: 'SET_ERROR', payload: null });
            const data = await getSims(state.currentPage, ITEMS_PER_PAGE, filters);
            dispatch({ type: 'SET_USERS', payload: data.content });
            dispatch({ type: 'SET_TOTAL_PAGES', payload: data.totalPages });
            
            if (data.totalPages < state.currentPage) {
                dispatch({ 
                    type: 'SET_PAGE', 
                    payload: data.totalPages === 0 ? 1 : data.totalPages 
                });
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            console.error('Error fetching sims:', error);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [state.currentPage]);

    useEffect(() => {
        const newFilters = {};
        if (state.filters.emailFilter) newFilters.email = state.filters.emailFilter;
        if (state.filters.statusFilter) newFilters.status = state.filters.statusFilter;
        loadSims(newFilters);
    }, [state.filters, loadSims]);

    const handleFilterSubmit = handleSubmit((data) => {
        dispatch({ type: 'SET_FILTERS', payload: data });
    });

    const handlePageChange = useCallback((page) => {
        if (page === state.currentPage || page < 1 || page > state.totalPages) return;
        dispatch({ type: 'SET_PAGE', payload: page });
    }, [state.currentPage, state.totalPages]);

    const toggleDeleteModal = useCallback((simId) => {
        dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: simId });
    }, []);

    const handleDelete = async () => {
        try {
            const response = await deleteSim(state.selectedSimId);
            if (response) {
                dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: null });
                await loadSims();
            }
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: 'Error al eliminar la sim' });
            console.error('Error al eliminar la Sim: ', error);
        }
    };

    return (
        <div className="flex flex-col p-3">
            <div className="flex max-sm:flex-col items-center justify-between mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">SimCards</h1>
                <ButtonNew to="/simcards/add" />
            </div>

            <FilterForm onSubmit={handleFilterSubmit} register={register} />

            <hr className="my-2" />

            {state.isLoading ? (
                <div className="text-center py-4">Cargando...</div>
            ) : (
                <div className="relative overflow-x-auto shadow-lg sm:rounded-lg">
                    <table className="w-full text-sm text-left border border-gray-200">
                        <TableHeader headerOptions={TABLE_HEADERS}/>
                        <tbody>
                            <SimTable 
                                sims={state.sims} 
                                onDelete={toggleDeleteModal}
                                error={state.error}
                            />
                        </tbody>
                    </table>
                </div>
            )}

            {!state.error && (
                <CustomPaginator 
                    currentPage={state.currentPage} 
                    totalPages={state.totalPages} 
                    handlePageChange={handlePageChange}
                />
            )}

            <DeleteModal
                isOpen={state.deleteModalOpen}
                toggleDeleteModal={() => toggleDeleteModal(null)}
                deleteModal={handleDelete}
                nameParameter="simCard"
            />
        </div>
    );
};

export default Simcards;